import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import _, { debounce } from 'lodash';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { eadmissionAPI } from '../../apis';
import { useActionableTicketReminder } from '../../utils/hooks';
import { calculateRemToPx } from '../../utils/newBedStatusUtil';
import {
  ADMISSION_AED_VIEW_SHOW_DETAILS,
  ADMISSION_FILTER,
  ADMISSION_SOPD_VIEW_SHOW_DETAILS,
} from '../../comm/constant/storage';
import { userRoleFunc } from '../../utils/userRole';
import rou from '../../router';
import { E_ADMISSION_TAB_VALUES as tabValues } from '../../comm/constant';
import EmergencyAdmission from './EmergencyAdmission';
import TransferIn from './TransferIn';
import Header from './components/Header';
import AdmissionFilter from './components/Popups/AdmissionFilter';
import AEDView from './AEDView';
import SOPDView from './SOPDView';

const ADMISSION_TOPBAR_HEIGHT_REM = 4.375;
const TAB_CONTENT_TOP_PADDING_REM = 0.875;

// 按Header下拉顺序找到第一个当前可用的Admission tab。
const getFirstVisibleAdmissionTab = (eadViewVisible) => {
  if (eadViewVisible?.transferIn?.eadmVisible) {
    return tabValues.Pending;
  }

  if (eadViewVisible?.transferIn?.eadmVisible && eadViewVisible?.aedView) {
    return tabValues.AEDView;
  }

  if (eadViewVisible?.transferIn?.requestInVisible) {
    return tabValues.TransferInPending;
  }

  if (eadViewVisible?.sopdView) {
    return tabValues.SOPDView;
  }

  return null;
};

// 读取缓存tab时校验合法性，避免旧值请求错误tab。
const getInitialAdmissionTab = () => {
  const storageTabValue = sessionStorage.getItem('ADMISSION_TAB');
  if (_.values(tabValues).includes(storageTabValue)) {
    return storageTabValue;
  }

  sessionStorage.setItem('ADMISSION_TAB', tabValues.Pending);
  return tabValues.Pending;
};

const Admission = () => {
  const [tabValue, setTabValue] = useState(getInitialAdmissionTab);
  const [showBusStop, setShowBusStop] = useState({
    type: 'all',
    show: false,
  });
  const [forMyAction, setForMyAction] = useState(false);
  const [showAEDDetail, setShowAEDDetail] = useState(
    () => localStorage.getItem(ADMISSION_AED_VIEW_SHOW_DETAILS) === 'true'
  );
  const [showSOPDDetails, setShowSOPDDetails] = useState(
    () => localStorage.getItem(ADMISSION_SOPD_VIEW_SHOW_DETAILS) === 'true'
  );
  const [filterOpen, setFilterOpen] = useState(false);
  const admissionParamsRef = useRef();
  const [filter, setFilter] = useState(initAdmissionFilter);
  const [specialty, setSpecialty] = useState({ specialtyCodes: [], subSpecialtyCodes: [] });
  const [, needRefresh] = useActionableTicketReminder('msg', 'AD');
  const [busstopOptions, setBusstopOptions] = useState([]);
  const history = useHistory();
  const loginInfo = useSelector((state) => state.loginInfo);
  const fullScreen = useSelector((state) => state.fullScreenReducer);
  const sso = useSelector((state) => state.ssoReducer);
  const headerRef = useRef(null);
  const [tabContentHeight, setTabContentHeight] = useState(0);
  const validAdmissionTabValues = useMemo(() => _.values(tabValues), []);
  const isAEDView = useMemo(() => tabValue === tabValues.AEDView, [tabValue]);
  const isSOPDView = useMemo(() => tabValue === tabValues.SOPDView, [tabValue]);
  const isLocationView = useMemo(() => isAEDView || isSOPDView, [isAEDView, isSOPDView]);
  const isTransferInView = useMemo(
    () => [tabValues.TransferInPending, tabValues.TransferInCompleted].includes(tabValue),
    [tabValue]
  );
  const [eadViewVisible, setEADViewVisible] = useState({
    aedView: false,
    sopdView: false,
    // transferIn: false,
    transferIn: {
      eadmVisible: false,
      requestInVisible: false,
      requestInViewAll: false,
      defaultAdmType: '',
      defaultFromUnit: '',
    },
  });
  const [EADViewVisibleLoaded, setEADViewVisibleLoaded] = useState(false);

  // 切換 tab 時 filter 只保留 specialty，subSpecialty，sex，waitTime
  useEffect(() => {
    setFilter((preFilter) => ({
      ...admissionDefaultFilter(),
      specialtys: preFilter.specialtys,
      sex: preFilter.sex,
      waitTime: preFilter.waitTime,
      busstop_C: preFilter.busstop_C ?? [],
      busstop_PD: preFilter.busstop_PD ?? [],
    }));
  }, [tabValue]);

  // 更新缓存
  useEffect(() => {
    localStorage.setItem(ADMISSION_FILTER, JSON.stringify(filter));
  }, [filter]);

  useEffect(() => {
    localStorage.setItem(ADMISSION_AED_VIEW_SHOW_DETAILS, String(showAEDDetail));
  }, [showAEDDetail]);

  useEffect(() => {
    localStorage.setItem(ADMISSION_SOPD_VIEW_SHOW_DETAILS, String(showSOPDDetails));
  }, [showSOPDDetails]);

  // 防抖函數
  const debouncedSearch = useRef(
    debounce(async (key, value) => {
      setFilter((preFilter) => ({
        ...preFilter,
        [key]: value,
      }));
    }, 1000)
  ).current;

  // 清除副作用
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // input 的 onChange 事件
  const handleFilterSelect = useCallback(
    (key) => (value) => {
      switch (key) {
        case 'waitTime':
        case 'sex':
        case 'specialtys':
        case 'busstop_C':
        case 'busstop_PD':
        case 'aeNo':
          setFilter((preFilter) => ({
            ...preFilter,
            [key]: value,
          }));
          break;

        default:
          (async () => {
            await debouncedSearch(key, value);
          })();
          break;
      }
    },
    [debouncedSearch]
  );

  const getSpecialty = useCallback(async () => {
    try {
      const { result } = await eadmissionAPI.getSpecialty();
      setSpecialty(result);
    } catch (error) {}
  }, []);

  // bus stop filter现在只有e-admission tab和AED VIEW tab有，这两个type都是'PD'
  const getAdmBusstopList = useCallback(async () => {
    try {
      if (isSOPDView || isTransferInView) return;
      const params = {
        type: isAEDView ? 'PD' : tabValue,
      };
      const { result } = await eadmissionAPI.getAdmBusstopList(params);
      setBusstopOptions(result);
    } catch (error) {}
  }, [isSOPDView, isTransferInView, isAEDView, tabValue]);

  const getEADViewVisible = useCallback(async () => {
    try {
      setEADViewVisibleLoaded(false);
      const { result: AEDResult } = await eadmissionAPI.getAEDViewVisible();
      const { result: SOPDResult } = await eadmissionAPI.getSOPDViewVisible();
      const { result: transferInResult } = await eadmissionAPI.getTransferInVisible();
      setEADViewVisible({
        aedView: AEDResult?.visible === 1,
        sopdView: SOPDResult?.visible === 1,
        transferIn: {
          eadmVisible: transferInResult?.EADM_VIEW_VISIBLE === 1,
          requestInVisible: transferInResult?.REQUEST_IN_VIEW_VISIBLE === 1,
          requestInViewAll: transferInResult?.REQUEST_IN_VIEW_ALL === 1,
          defaultAdmType: transferInResult?.DEFAULT_ADMISSION_TYPE ?? '',
          defaultFromUnit: transferInResult?.DEFAULT_FROM_UNIT ?? '',
        },
      });
      setEADViewVisibleLoaded(true);
    } catch (error) {}
  }, []);

  useEffect(() => {
    getEADViewVisible();
  }, [getEADViewVisible, loginInfo]);

  // tab值非法时回退到第一个可用tab。
  useEffect(() => {
    if (!EADViewVisibleLoaded || validAdmissionTabValues.includes(tabValue)) {
      return;
    }

    const fallbackTab = getFirstVisibleAdmissionTab(eadViewVisible);
    if (fallbackTab) {
      sessionStorage.setItem('ADMISSION_TAB', fallbackTab);
      setTabValue(fallbackTab);
    }
  }, [eadViewVisible, EADViewVisibleLoaded, tabValue, validAdmissionTabValues]);

  // 当前tab对应功能关闭时，自动退回第一个可用tab。
  useEffect(() => {
    if (!EADViewVisibleLoaded) {
      return;
    }

    const currentViewClosed =
      ([tabValues.Pending, tabValues.Completed].includes(tabValue) && !eadViewVisible.transferIn?.eadmVisible) ||
      (tabValue === tabValues.AEDView && !(eadViewVisible.transferIn?.eadmVisible && eadViewVisible.aedView)) ||
      (tabValue === tabValues.SOPDView && !eadViewVisible.sopdView) ||
      (isTransferInView && !eadViewVisible.transferIn?.requestInVisible);

    if (currentViewClosed) {
      const fallbackTab = getFirstVisibleAdmissionTab(eadViewVisible);
      if (fallbackTab) {
        sessionStorage.setItem('ADMISSION_TAB', fallbackTab);
        setTabValue(fallbackTab);
      }
    }
  }, [eadViewVisible, EADViewVisibleLoaded, isTransferInView, tabValue]);

  useEffect(() => {
    getAdmBusstopList();
  }, [getAdmBusstopList]);

  useEffect(() => {
    getSpecialty();
  }, [getSpecialty]);

  // 根据全局topbar和Admission header高度，限制tab内容只占一屏剩余空间。
  useEffect(() => {
    const updateTabContentHeight = () => {
      const pageHeight = document.documentElement?.clientHeight || window.innerHeight || 0;
      const topbarHeight = fullScreen || sso ? 0 : calculateRemToPx(ADMISSION_TOPBAR_HEIGHT_REM);
      const headerHeight = headerRef?.current ? headerRef.current.getBoundingClientRect().height : 0;

      setTabContentHeight(Math.max(0, Math.round(pageHeight - topbarHeight - headerHeight)));
    };

    updateTabContentHeight();
    window.addEventListener('resize', updateTabContentHeight);

    const resizeObserver =
      window.ResizeObserver && headerRef.current ? new ResizeObserver(updateTabContentHeight) : null;
    resizeObserver?.observe(headerRef.current);

    return () => {
      window.removeEventListener('resize', updateTabContentHeight);
      resizeObserver?.disconnect();
    };
  }, [fullScreen, sso, tabValue, eadViewVisible]);

  const specialtyList = useMemo(() => {
    return _.uniqBy(
      _.concat(
        _.map(_.concat(specialty.specialtyCodes, specialty.subSpecialtyCodes), (item) => ({
          text: item,
          value: item,
        })),
        filter.specialtys
      ),
      'value'
    );
  }, [specialty, filter.specialtys]);

  const busstopList = useMemo(() => {
    return _.map(busstopOptions, (item) => ({
      text: item.value,
      value: item.key,
    }));
  }, [busstopOptions]);
  const paddedTabContentHeight = useMemo(() => {
    return Math.max(0, tabContentHeight - calculateRemToPx(TAB_CONTENT_TOP_PADDING_REM));
  }, [tabContentHeight]);

  const providerValue = useMemo(
    () => ({
      tabValue,
      setTabValue: (value) => {
        sessionStorage.setItem('ADMISSION_TAB', value);
        setTabValue(value);
      },
      tabValues,
      showBusStop,
      setShowBusStop,
      forMyAction,
      setForMyAction,
      showAEDDetail,
      setShowAEDDetail,
      showSOPDDetails,
      setShowSOPDDetails,
      filter,
      setFilter,
      setFilterOpen,
      admissionParamsRef,
      initFilter: admissionDefaultFilter,
      specialty,
      getSpecialty,
      specialtyList,
      handleFilterSelect,
      needRefresh,
      busstopList,
      eadViewVisible,
    }),
    [
      tabValue,
      setTabValue,
      tabValues,
      showBusStop,
      setShowBusStop,
      forMyAction,
      setForMyAction,
      showAEDDetail,
      setShowAEDDetail,
      showSOPDDetails,
      setShowSOPDDetails,
      filter,
      setFilter,
      setFilterOpen,
      admissionParamsRef,
      specialty,
      getSpecialty,
      specialtyList,
      handleFilterSelect,
      needRefresh,
      busstopList,
      eadViewVisible,
    ]
  );

  // 如果在当前页面切换了用户或者角色，且没有权限访问admission，则跳转到home page
  useEffect(() => {
    const hasAdmissionPermission = userRoleFunc?.getIsShowAdmission();
    // logout的状态也需要能看到admission
    if (!hasAdmissionPermission && loginInfo?.flag) {
      history.push(rou.homeRou.path);
    }
  }, [loginInfo]);

  return (
    <AdmissionContext.Provider value={providerValue}>
      <Header ref={headerRef} isLocationView={isLocationView} />
      {/* transfer in */}
      {isTransferInView && <TransferIn contentHeight={tabContentHeight} />}

      {/* e-admission 的pending tab和complete tab */}
      {[tabValues.Pending, tabValues.Completed].includes(tabValue) && (
        <EmergencyAdmission contentHeight={paddedTabContentHeight} />
      )}

      {/* aed view */}
      {isAEDView && <AEDView headerRef={headerRef} contentHeight={tabContentHeight} />}

      {/* sopd view */}
      {isSOPDView && <SOPDView headerRef={headerRef} contentHeight={tabContentHeight} />}

      {/* Popup */}
      {filterOpen && (
        <AdmissionFilter
          open={filterOpen}
          handleClose={() => setFilterOpen(false)}
          handleUpdate={(data) => {
            setFilter({ ...data });
            setFilterOpen(false);
          }}
          tabValue={tabValue}
          initFilter={filter}
        />
      )}
    </AdmissionContext.Provider>
  );
};

export const AdmissionContext = createContext('');

export const admissionDefaultFilter = () => ({
  aeNo: '',
  chiName: '',
  diagnosis: '',
  engName: '',
  handingByIds: [],
  priority: [],
  remark: '',
  sex: '',
  specialtys: [],
  status: [],
  hospCode: '',
  startDate: moment().add(-2, 'days'),
  endDate: moment(),
  waitTime: '',
  busstop_C: [],
  busstop_PD: [],
});

export const initAdmissionFilter = () => {
  const filterData = JSON.parse(localStorage.getItem(ADMISSION_FILTER));
  if (filterData) {
    return {
      ...admissionDefaultFilter(),
      specialtys: filterData.specialtys ?? [],
      sex: filterData.sex,
      waitTime: filterData.waitTime,
      busstop_C: filterData.busstop_C ?? [],
      busstop_PD: filterData.busstop_PD ?? [],
    };
  } else {
    localStorage.setItem(ADMISSION_FILTER, JSON.stringify(admissionDefaultFilter()));
    return { ...admissionDefaultFilter() };
  }
};

export default Admission;
